import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Card, CardContent, CardDescription } from "@/components/ui/v2/card";
import { metricCardIconClassName, metricCardVariants } from "@/components/ui/v2/metric-card-variants";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/v2/tooltip";
import { cn } from "@/lib/utils/cn";
import { useValueFormatter } from "@/utils/useValueFormatter";

type MetricCardProps = {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  /**
   * Optional context below the value, already composed (e.g. "5 vacancies").
   * Prefer `subtitleCount` when the context is "<number> <label>" — it formats the number for you.
   */
  subtitle?: string;
  /**
   * Count-plus-label context, e.g. { count: 1819, labelKey: "reporting.metricsImpressions" }
   * or { count: 12, total: 24500, labelKey: "common.countOfTotal" }.
   *
   * The card groups every figure (1.819) so a subtitle reads like the table column showing the
   * same number; passing a pre-interpolated string instead is how ungrouped counts slip through.
   */
  subtitleCount?: {
    count: number;
    /** Optional denominator, for "<count> of <total>" style labels. */
    total?: number;
    /**
     * i18n key taking `{{count}}` (raw, for pluralisation), `{{formattedCount}}` (display) and,
     * when `total` is given, `{{total}}` (display).
     */
    labelKey: string;
  };
  variant?: "default" | "gradient" | "foreground" | "primary" | "background";
  isLoading?: boolean;
  action?: ReactNode;
  /** When true, the subtitle wraps instead of truncating. */
  subtitleWrap?: boolean;
  /** Tooltip shown when hovering the subtitle text. */
  subtitleTooltip?: string;
};

function MetricCard({
  icon,
  label,
  value,
  subtitle,
  subtitleCount,
  variant = "default",
  isLoading = false,
  action,
  subtitleWrap = false,
  subtitleTooltip,
}: MetricCardProps) {
  const { t } = useTranslation();
  const { format } = useValueFormatter();
  const resolvedSubtitle = subtitleCount
    ? t(subtitleCount.labelKey, {
      count: subtitleCount.count,
      formattedCount: format(subtitleCount.count, "whole"),
      ...(subtitleCount.total !== undefined && {
        total: format(subtitleCount.total, "whole"),
      }),
    })
    : subtitle;
  const hasSubtitle = !!resolvedSubtitle?.trim();

  return (
    <Card data-slot="metric-card" data-variant={variant} className={cn(metricCardVariants({ variant }), action && "relative")}>
      {action && <div className="absolute right-2 top-2 z-10">{action}</div>}
      <CardContent className="p-3 lg:p-2">
        <div className="flex items-center gap-2 lg:gap-3">
          <div data-slot="metric-card-icon" className={cn(metricCardIconClassName, "ml-4")}>
            {icon}
          </div>
          <div
            className={`flex flex-col gap-0 flex-1 min-w-0 leading-tight ${hasSubtitle ? (subtitleWrap ? "justify-center" : "h-11 lg:h-12 justify-center") : ""}`}
          >
            <CardDescription className="text-xs lg:text-sm tracking-wide font-medium truncate shrink-0">
              {label}
            </CardDescription>
            <p
              data-slot="metric-card-value"
              data-loading={isLoading || undefined}
              className={`text-sm lg:text-xl font-semibold text-foreground truncate min-h-0 shrink-0 ${isLoading ? "animate-pulse text-muted-foreground" : ""}`}
            >
              {value}
            </p>
            {hasSubtitle && (
              subtitleTooltip ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className={`text-xs lg:text-sm text-muted-foreground shrink-0 cursor-default underline decoration-dotted underline-offset-2 ${subtitleWrap ? "leading-tight" : "truncate"}`}>
                        {resolvedSubtitle}
                      </p>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[280px]">
                      <p>{subtitleTooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <p className={`text-xs lg:text-sm text-muted-foreground shrink-0 ${subtitleWrap ? "leading-tight" : "truncate"}`}>
                  {resolvedSubtitle}
                </p>
              )
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default MetricCard;
